import type { TileResourceCosts, TileResourceData } from '@/plugins/tiles/contracts'
import { MESHOPT_EXTENSION, validateMeshoptViews } from '@/plugins/tiles/mesh/meshoptViews'
import type { TextureSizing } from '@/plugins/tiles/mesh/textureSizing'
import { getTextureDimensions, resolveTextureSizing } from '@/plugins/tiles/mesh/textureSizing'
import { TilesError } from '@/plugins/tiles/TilesError'

/** GLTFLoader 使用的原始 JSON；所有进入解码路径的关键字段由预检校验。 */
export type GltfJson = Record<string, any>

export interface GltfManifest {
  readonly json: GltfJson
  readonly binary?: Uint8Array<ArrayBuffer>
  readonly warnings: readonly string[]
  readonly meshoptBytes: number
  readonly dracoBytes?: number
}

const COMPONENT_BYTE_SIZES: Readonly<Record<number, number>> = {
  5120: 1,
  5121: 1,
  5122: 2,
  5123: 2,
  5125: 4,
  5126: 4,
}
const ACCESSOR_COMPONENT_COUNTS: Readonly<Record<string, number>> = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16,
}

function fail(message: string, extension?: string, unsupported = false): never {
  throw new TilesError({
    code: unsupported ? 'unsupported-capability' : 'invalid-resource',
    stage: 'decode',
    message,
    extension,
    retryable: false,
  })
}

function record(value: unknown): value is GltfJson {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function list(json: GltfJson, key: string): any[] {
  if (json[key] === undefined) return []
  if (!Array.isArray(json[key])) fail(`glTF ${key} 必须是数组`)
  return json[key]
}

/** 分别校验容器与内部 JSON；不把 GLB v2 误当成内容能力通过。 */
export function readGltfManifest(
  resource: TileResourceData,
  ktx2 = false,
  meshopt = false,
  optionalMetadataPolicy: 'reject' | 'geometry-only' = 'reject',
  draco = false
): GltfManifest {
  if (!resource.complete) fail('不能从资源前缀解码 glTF')
  const view = new DataView(resource.data)
  let jsonBytes = new Uint8Array(resource.data)
  let binary: Uint8Array<ArrayBuffer> | undefined
  if (resource.data.byteLength >= 4 && view.getUint32(0, true) === 0x46546c67) {
    if (resource.data.byteLength < 20) fail('GLB 头或 JSON chunk 不完整')
    if (view.getUint32(4, true) !== 2) {
      throw new TilesError({
        code: 'unsupported-version',
        stage: 'decode',
        message: '仅支持 GLB 2',
        retryable: false,
      })
    }
    if (view.getUint32(8, true) !== resource.data.byteLength) fail('GLB 声明长度与响应不一致')
    let foundJson = false
    for (let offset = 12; offset < resource.data.byteLength; ) {
      if (offset + 8 > resource.data.byteLength) fail('GLB chunk 头截断')
      const length = view.getUint32(offset, true)
      const type = view.getUint32(offset + 4, true)
      const end = offset + 8 + length
      if (length % 4 !== 0 || end > resource.data.byteLength) fail('GLB chunk 对齐或长度无效')
      if (offset === 12 && type !== 0x4e4f534a) fail('GLB 首 chunk 必须是 JSON')
      if (type === 0x4e4f534a) {
        if (foundJson) fail('GLB 存在重复 JSON chunk')
        foundJson = true
        jsonBytes = new Uint8Array(resource.data, offset + 8, length)
      } else if (type === 0x004e4942) {
        if (binary) fail('GLB 存在重复 BIN chunk')
        binary = new Uint8Array(resource.data, offset + 8, length)
      }
      offset = end
    }
  }
  let json: unknown
  try {
    json = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(jsonBytes))
  } catch (cause) {
    throw new TilesError(
      { code: 'invalid-resource', stage: 'decode', message: 'glTF JSON 无效', retryable: false },
      { cause }
    )
  }
  if (!record(json) || !record(json.asset)) fail('glTF asset 声明缺失')
  if (
    json.asset.version !== '2.0' ||
    (json.asset.minVersion !== undefined && json.asset.minVersion !== '2.0')
  ) {
    throw new TilesError({
      code: 'unsupported-version',
      stage: 'decode',
      message: '仅支持 glTF asset.version/minVersion 2.0',
      retryable: false,
    })
  }
  const warnings = validateGltfProfile(json, ktx2, meshopt, optionalMetadataPolicy, draco)
  const meshoptBytes = validateMeshoptViews(json, !!binary)
  const dracoBytes = draco
    ? (json.meshes ?? []).reduce(
        (sum: number, mesh: any) =>
          sum +
          mesh.primitives.reduce((part: number, primitive: any) => {
            const index = primitive.extensions?.KHR_draco_mesh_compression?.bufferView
            return part + (index === undefined ? 0 : (json.bufferViews?.[index]?.byteLength ?? 0))
          }, 0),
        0
      )
    : 0
  return { json, binary, warnings, meshoptBytes, dracoBytes }
}

/** 明确已知、规范允许 core 回退的可选材质扩展；不会启用 Three 的高级材质插件。 */
const MATERIAL_FALLBACKS = new Set([
  'KHR_materials_clearcoat',
  'KHR_materials_sheen',
  'KHR_materials_specular',
  'KHR_materials_ior',
  'KHR_materials_emissive_strength',
])

function validateGltfProfile(
  json: GltfJson,
  ktx2: boolean,
  meshopt: boolean,
  optionalMetadataPolicy: 'reject' | 'geometry-only',
  draco: boolean
): string[] {
  const metadata = new Set(['EXT_mesh_features', 'EXT_structural_metadata'])
  const supported = (name: string) =>
    name === 'KHR_materials_unlit' ||
    name === 'KHR_mesh_quantization' ||
    name === 'EXT_texture_webp' ||
    (meshopt && name === MESHOPT_EXTENSION) ||
    (draco && name === 'KHR_draco_mesh_compression') ||
    (ktx2 && name === 'KHR_texture_basisu')
  const used = list(json, 'extensionsUsed')
  const required = list(json, 'extensionsRequired')
  if ([...used, ...required].some((value) => typeof value !== 'string'))
    fail('glTF 扩展列表必须包含字符串')
  for (const name of required) {
    if (!used.includes(name)) fail('extensionsRequired 必须包含于 extensionsUsed', name)
    if (!supported(name)) fail(`未装配必需扩展 ${name}`, name, true)
  }
  const warnings: string[] = []
  const inspectExtensions = (value: unknown, scope: string): void => {
    if (!record(value)) return
    if (value.extensions !== undefined) {
      if (!record(value.extensions)) fail('glTF extensions 必须是对象')
      for (const name of Object.keys(value.extensions)) {
        if (!used.includes(name)) fail('使用的扩展未列入 extensionsUsed', name)
        if (name === 'KHR_materials_unlit' && scope === 'material') continue
        if (name === 'EXT_texture_webp' && scope === 'textures') continue
        if (ktx2 && name === 'KHR_texture_basisu' && scope === 'textures') continue
        if (meshopt && name === MESHOPT_EXTENSION && ['buffers', 'bufferViews'].includes(scope))
          continue
        if (draco && name === 'KHR_draco_mesh_compression' && scope === 'primitives') continue
        if (
          optionalMetadataPolicy === 'geometry-only' &&
          metadata.has(name) &&
          !required.includes(name) &&
          ((name === 'EXT_mesh_features' && scope === 'primitives') ||
            (name === 'EXT_structural_metadata' && scope === 'root'))
        ) {
          warnings.push(`可选扩展 ${name} 仅几何预览，未提供建筑属性或 feature ID 拾取`)
          continue
        }
        if (scope === 'material' && MATERIAL_FALLBACKS.has(name) && !required.includes(name)) {
          warnings.push(`可选扩展 ${name} 使用 core metallic-roughness 回退`)
          continue
        }
        fail(`M0 不支持 ${name}`, name, true)
      }
    }
    for (const [key, child] of Object.entries(value)) {
      if (key === 'extras' || key === 'extensions') continue
      if (Array.isArray(child))
        child.forEach((item) => inspectExtensions(item, key === 'materials' ? 'material' : key))
      else inspectExtensions(child, key)
    }
  }
  inspectExtensions(json, 'root')
  for (const name of used) {
    if (
      optionalMetadataPolicy === 'geometry-only' &&
      metadata.has(name) &&
      !required.includes(name)
    )
      continue
    if (!supported(name) && !MATERIAL_FALLBACKS.has(name)) fail(`M0 不支持扩展 ${name}`, name, true)
  }
  // 已验证允许回退后才移除可选扩展，避免 GLTFLoader 自动启用更宽能力。
  for (const material of list(json, 'materials')) {
    if (!record(material)) fail('glTF material 无效')
    if (material.extensions) {
      material.extensions = Object.fromEntries(
        Object.entries(material.extensions).filter(([name]) => name === 'KHR_materials_unlit')
      )
    }
    if (
      material.alphaMode !== undefined &&
      !['OPAQUE', 'MASK', 'BLEND'].includes(material.alphaMode)
    )
      fail('glTF alphaMode 无效')
  }
  json.extensionsUsed = used.filter((name) => supported(name))
  // 扩展图片交给受控解码路径；避免 Three 内置插件绕过 Tiles 数据源。
  json.extensionsRequired = required.filter(
    (name) => name !== 'EXT_texture_webp' && !(ktx2 && name === 'KHR_texture_basisu')
  )
  for (const texture of list(json, 'textures')) {
    const webp = texture.extensions?.EXT_texture_webp
    const basis = ktx2 ? texture.extensions?.KHR_texture_basisu : undefined
    if (webp !== undefined) {
      if (!record(webp) || !Number.isSafeInteger(webp.source) || !json.images?.[webp.source])
        fail('EXT_texture_webp.source 无效', 'EXT_texture_webp')
      const image = json.images[webp.source]
      if (image.mimeType !== undefined && image.mimeType !== 'image/webp')
        fail('EXT_texture_webp 图片 MIME 无效', 'EXT_texture_webp')
      image.mimeType = 'image/webp'
    }
    if (
      basis !== undefined &&
      (!Number.isSafeInteger(basis.source) || !json.images?.[basis.source])
    )
      fail('KHR_texture_basisu.source 无效')
    if (basis !== undefined || webp !== undefined) {
      // 与 Three 的内置扩展优先级一致：已装配 KTX2 时先选 KTX2。
      texture.source = basis?.source ?? webp.source
      texture.extensions = {}
    }
  }
  if (list(json, 'skins').length || list(json, 'animations').length)
    fail('M0 不支持蒙皮或动画', undefined, true)
  const nodes = list(json, 'nodes')
  nodes.forEach((node) => {
    if (!record(node)) fail('glTF node 无效')
    if (node.skin !== undefined || node.weights !== undefined)
      fail('M0 不支持节点蒙皮或 morph', undefined, true)
  })
  const accessors = list(json, 'accessors')
  accessors.forEach((accessor) => {
    if (!record(accessor) || !Number.isSafeInteger(accessor.count) || accessor.count < 1)
      fail('glTF accessor.count 无效')
    if (![5120, 5121, 5122, 5123, 5125, 5126].includes(accessor.componentType))
      fail('glTF accessor.componentType 无效')
    if (!['SCALAR', 'VEC2', 'VEC3', 'VEC4', 'MAT2', 'MAT3', 'MAT4'].includes(accessor.type))
      fail('glTF accessor.type 无效')
  })
  const meshes = list(json, 'meshes')
  if (!meshes.length) fail('glTF 不包含静态 Mesh', undefined, true)
  for (const mesh of meshes) {
    if (!record(mesh) || !Array.isArray(mesh.primitives) || !mesh.primitives.length)
      fail('glTF mesh.primitives 无效')
    if (mesh.weights !== undefined) fail('M0 不支持 morph weights', undefined, true)
    for (const primitive of mesh.primitives) {
      if (!record(primitive) || !record(primitive.attributes))
        fail('glTF primitive.attributes 无效')
      if (![4, 5, 6].includes(primitive.mode ?? 4))
        fail('M0 仅支持 TRIANGLES/TRIANGLE_STRIP/TRIANGLE_FAN', undefined, true)
      if (primitive.targets !== undefined) fail('M0 不支持 morph targets', undefined, true)
      const compressed = draco ? primitive.extensions?.KHR_draco_mesh_compression : undefined
      if (compressed) {
        const extension = 'KHR_draco_mesh_compression'
        const viewIndex = compressed.bufferView
        const view = json.bufferViews?.[viewIndex]
        const buffer = json.buffers?.[view?.buffer]
        const offset = view?.byteOffset ?? 0
        if (
          !Number.isSafeInteger(viewIndex) ||
          !record(view) ||
          !record(buffer) ||
          !Number.isSafeInteger(offset) ||
          offset < 0 ||
          !Number.isSafeInteger(view.byteLength) ||
          view.byteLength < 1 ||
          !Number.isSafeInteger(buffer.byteLength) ||
          !Number.isSafeInteger(offset + view.byteLength) ||
          offset + view.byteLength > buffer.byteLength
        )
          fail('KHR_draco_mesh_compression 压缩视图越界', extension)
        if (!record(compressed.attributes) || !Object.keys(compressed.attributes).length)
          fail('KHR_draco_mesh_compression 属性映射无效', extension)
        const ids = new Set<number>()
        for (const [semantic, id] of Object.entries(compressed.attributes)) {
          if (
            !Number.isSafeInteger(id) ||
            (id as number) < 0 ||
            ids.has(id as number) ||
            !accessors[primitive.attributes[semantic]]
          )
            fail('KHR_draco_mesh_compression 属性 ID/accessor 无效', extension)
          ids.add(id as number)
        }
        if ((primitive.mode ?? 4) === 6)
          fail('KHR_draco_mesh_compression 不支持 TRIANGLE_FAN', extension, true)
      }
      const position = accessors[primitive.attributes.POSITION]
      const quantized = required.includes('KHR_mesh_quantization')
      if (
        !position ||
        position.type !== 'VEC3' ||
        !(quantized ? [5120, 5121, 5122, 5123, 5126] : [5126]).includes(position.componentType)
      )
        fail('glTF POSITION 必须为 Float32 VEC3 或合法量化 VEC3')
      for (const index of Object.values(primitive.attributes)) {
        if (!Number.isSafeInteger(index) || !accessors[index as number])
          fail('glTF attribute accessor 索引无效')
      }
      for (const [semantic, index] of Object.entries(primitive.attributes)) {
        const accessor = accessors[index as number]
        if (compressed && !(semantic in compressed.attributes) && accessor.bufferView === undefined)
          fail(
            'KHR_draco_mesh_compression 额外属性缺少普通 bufferView',
            'KHR_draco_mesh_compression'
          )
        if (
          semantic === 'POSITION' ||
          semantic === 'NORMAL' ||
          semantic === 'TANGENT' ||
          /^TEXCOORD_\d+$/.test(semantic)
        ) {
          const normal = semantic === 'NORMAL' || semantic === 'TANGENT'
          const texcoord = semantic.startsWith('TEXCOORD_')
          const expectedType = texcoord ? 'VEC2' : semantic === 'TANGENT' ? 'VEC4' : 'VEC3'
          const float = accessor.componentType === 5126 && !accessor.normalized
          const integer =
            quantized &&
            (normal ? [5120, 5122] : [5120, 5121, 5122, 5123]).includes(accessor.componentType) &&
            (!normal || accessor.normalized === true)
          const coreUV =
            texcoord &&
            [5121, 5123].includes(accessor.componentType) &&
            accessor.normalized === true
          if (accessor.type !== expectedType || !(float || integer || coreUV))
            fail(`glTF ${semantic} 类型或 normalized 无效`)
        }
      }
      const index = primitive.indices === undefined ? undefined : accessors[primitive.indices]
      if (
        primitive.indices !== undefined &&
        (!index || index.type !== 'SCALAR' || ![5121, 5123, 5125].includes(index.componentType))
      )
        fail('glTF indices accessor 无效')
      const count = index?.count ?? position.count
      if (count < 3 || ((primitive.mode ?? 4) === 4 && count % 3 !== 0))
        fail('glTF 三角拓扑数量无效')
    }
  }
  for (const buffer of list(json, 'buffers')) {
    if (!record(buffer) || !Number.isSafeInteger(buffer.byteLength) || buffer.byteLength < 1)
      fail('glTF buffer.byteLength 无效')
    if (buffer.uri !== undefined && (typeof buffer.uri !== 'string' || buffer.uri.length === 0)) {
      fail('glTF buffer.uri 必须是非空字符串；GLB 内嵌 buffer 须省略 uri')
    }
  }
  for (const view of list(json, 'bufferViews')) {
    if (!record(view)) {
      fail('glTF bufferView 必须是对象')
    }
  }
  for (const image of list(json, 'images')) {
    if (
      !record(image) ||
      (typeof image.uri !== 'string' && !Number.isSafeInteger(image.bufferView))
    )
      fail('glTF image 缺少 URI 或 bufferView')
    if (image.uri !== undefined && image.bufferView !== undefined)
      fail('glTF image.uri 与 bufferView 互斥')
    if (
      image.mimeType !== undefined &&
      !['image/png', 'image/jpeg', 'image/webp', ...(ktx2 ? ['image/ktx2'] : [])].includes(
        image.mimeType
      )
    )
      fail(`M0 不支持图片 ${image.mimeType}`, undefined, true)
  }
  validateAccessorLayouts(json)
  // 结构校验完成后再剥离元数据，畸形 mesh/primitive 必须走统一诊断。
  if (optionalMetadataPolicy === 'geometry-only') {
    if (json.extensions) {
      json.extensions = Object.fromEntries(
        Object.entries(json.extensions).filter(([name]) => !metadata.has(name))
      )
    }
    for (const mesh of meshes) {
      for (const primitive of mesh.primitives) {
        if (primitive.extensions) {
          primitive.extensions = Object.fromEntries(
            Object.entries(primitive.extensions).filter(([name]) => !metadata.has(name))
          )
        }
      }
    }
  }
  return [...new Set(warnings)]
}

function validateAccessorLayouts(json: GltfJson): void {
  for (const accessor of json.accessors ?? []) {
    if (accessor.normalized !== undefined && typeof accessor.normalized !== 'boolean')
      fail('accessor.normalized 无效')
    if (accessor.bufferView === undefined) continue
    const view = json.bufferViews?.[accessor.bufferView]
    const size = COMPONENT_BYTE_SIZES[accessor.componentType]
    const element = size * ACCESSOR_COMPONENT_COUNTS[accessor.type]
    const offset = accessor.byteOffset ?? 0
    const stride = view?.byteStride ?? element
    const end = offset + (accessor.count - 1) * stride + element
    if (
      !Number.isSafeInteger(accessor.bufferView) ||
      !view ||
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      offset % size !== 0 ||
      !Number.isSafeInteger(stride) ||
      stride < element ||
      stride % size !== 0 ||
      !Number.isSafeInteger(end) ||
      end > view.byteLength
    )
      fail('glTF accessor 布局越界或未对齐')
  }
}

/** 数据 URI 在内存解码，不占网络队列。 */
export function decodeDataUri(uri: string): ArrayBuffer {
  const match = /^data:([^,]*),(.*)$/s.exec(uri)
  if (!match) fail('无效 data URI')
  try {
    if (/;base64$/i.test(match[1]))
      return Uint8Array.from(atob(match[2]), (char) => char.charCodeAt(0)).buffer
    const data = match[2]
    const values: number[] = []
    for (let i = 0; i < data.length; i++) {
      if (data[i] === '%') {
        const hex = data.slice(i + 1, i + 3)
        if (!/^[\da-f]{2}$/i.test(hex)) fail('data URI 百分号编码无效')
        values.push(parseInt(hex, 16))
        i += 2
      } else values.push(data.charCodeAt(i))
    }
    return new Uint8Array(values).buffer
  } catch (cause) {
    throw new TilesError(
      { code: 'invalid-resource', stage: 'decode', message: 'data URI 解码失败', retryable: false },
      { cause }
    )
  }
}

/** PNG/JPEG/WebP 头预估解码尺寸；实际图片解码仍由浏览器完成。 */
export function readImageInfo(data: ArrayBuffer | Uint8Array): {
  mimeType: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/ktx2'
  width: number
  height: number
} {
  const view =
    data instanceof ArrayBuffer
      ? new DataView(data)
      : new DataView(data.buffer, data.byteOffset, data.byteLength)
  if (
    data.byteLength >= 80 &&
    view.getUint32(0) === 0xab4b5458 &&
    view.getUint32(4) === 0x203230bb &&
    view.getUint32(8) === 0x0d0a1a0a
  ) {
    const width = view.getUint32(20, true)
    const height = view.getUint32(24, true)
    if (
      !width ||
      !height ||
      view.getUint32(28, true) ||
      view.getUint32(32, true) ||
      view.getUint32(36, true) !== 1 ||
      !view.getUint32(40, true)
    )
      fail('KTX2 二维图片头无效')
    return { mimeType: 'image/ktx2', width, height }
  }
  if (
    data.byteLength >= 24 &&
    view.getUint32(0) === 0x89504e47 &&
    view.getUint32(4) === 0x0d0a1a0a &&
    view.getUint32(12) === 0x49484452
  ) {
    const width = view.getUint32(16)
    const height = view.getUint32(20)
    if (!width || !height) fail('PNG 尺寸无效')
    return { mimeType: 'image/png', width, height }
  }
  if (data.byteLength >= 4 && view.getUint16(0) === 0xffd8) {
    let offset = 2
    while (offset + 4 <= data.byteLength) {
      if (view.getUint8(offset) !== 0xff) fail('JPEG marker 无效')
      while (offset + 1 < data.byteLength && view.getUint8(offset + 1) === 0xff) offset++
      const marker = view.getUint8(offset + 1)
      if (marker === 0xda || marker === 0xd9) break
      const length = view.getUint16(offset + 2)
      if (length < 2 || offset + 2 + length > data.byteLength) fail('JPEG segment 截断')
      if ([0xc0, 0xc1, 0xc2].includes(marker) && length >= 8) {
        const height = view.getUint16(offset + 5)
        const width = view.getUint16(offset + 7)
        if (!width || !height) fail('JPEG 尺寸无效')
        return { mimeType: 'image/jpeg', width, height }
      }
      offset += length + 2
    }
    fail('JPEG 缺少受支持的 SOF')
  }
  if (
    data.byteLength >= 20 &&
    view.getUint32(0) === 0x52494646 &&
    view.getUint32(8) === 0x57454250
  ) {
    const riffEnd = view.getUint32(4, true) + 8
    const chunkSize = view.getUint32(16, true)
    if (riffEnd > data.byteLength || chunkSize + 20 > riffEnd) fail('WebP RIFF 或 chunk 截断')
    const chunk = view.getUint32(12)
    let width = 0
    let height = 0
    if (chunk === 0x56503858 && chunkSize >= 10) {
      width = 1 + view.getUint8(24) + (view.getUint8(25) << 8) + (view.getUint8(26) << 16)
      height = 1 + view.getUint8(27) + (view.getUint8(28) << 8) + (view.getUint8(29) << 16)
    } else if (
      chunk === 0x56503820 &&
      chunkSize >= 10 &&
      view.getUint8(23) === 0x9d &&
      view.getUint8(24) === 0x01 &&
      view.getUint8(25) === 0x2a
    ) {
      width = view.getUint16(26, true) & 0x3fff
      height = view.getUint16(28, true) & 0x3fff
    } else if (chunk === 0x5650384c && chunkSize >= 5 && view.getUint8(20) === 0x2f) {
      width = 1 + view.getUint8(21) + ((view.getUint8(22) & 0x3f) << 8)
      height =
        1 + (view.getUint8(22) >> 6) + (view.getUint8(23) << 2) + ((view.getUint8(24) & 0x0f) << 10)
    }
    if (width < 1 || height < 1) fail('WebP 尺寸或编码头无效')
    return { mimeType: 'image/webp', width, height }
  }
  fail('M0 图片必须是有效 PNG/JPEG/WebP', undefined, true)
}

export function getBufferView(
  json: GltfJson,
  buffers: readonly (ArrayBuffer | Uint8Array<ArrayBuffer>)[],
  index: number
): ArrayBuffer {
  return getBufferViewBytes(json, buffers, index).slice().buffer
}

/** 只建立借用视图，用于头探测和范围校验，不在预算预约之前复制整个 BIN。 */
export function getBufferViewBytes(
  json: GltfJson,
  buffers: readonly (ArrayBuffer | Uint8Array<ArrayBuffer>)[],
  index: number
): Uint8Array<ArrayBuffer> {
  const definition = json.bufferViews?.[index]
  if (!record(definition)) fail('glTF bufferView 引用无效')
  const buffer = buffers[definition.buffer]
  const offset = definition.byteOffset ?? 0
  if (
    !buffer ||
    !Number.isSafeInteger(definition.buffer) ||
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    !Number.isSafeInteger(definition.byteLength) ||
    definition.byteLength < 1 ||
    offset + definition.byteLength > buffer.byteLength
  )
    fail('glTF bufferView 范围越界')
  return buffer instanceof ArrayBuffer
    ? new Uint8Array(buffer, offset, definition.byteLength)
    : buffer.subarray(offset, offset + definition.byteLength)
}

export function estimateGltfGeometryBytes(json: GltfJson, draco = false): number {
  let bytes = 0
  // 与 Three 的 InterleavedBuffer 身份对齐：视图、分量类型、起始切片及 count。
  // 同一上传缓冲只计一次；不同切片/count 即使共享 CPU 数据也分别计费。
  const interleaved = new Set<string>()
  for (const accessor of json.accessors ?? []) {
    const view = json.bufferViews?.[accessor.bufferView]
    const element =
      ACCESSOR_COMPONENT_COUNTS[accessor.type] * COMPONENT_BYTE_SIZES[accessor.componentType]
    const stride = view?.byteStride
    if (stride && stride !== element) {
      const slice = Math.floor((accessor.byteOffset ?? 0) / stride)
      const key = `${accessor.bufferView}:${accessor.componentType}:${slice}:${accessor.count}`
      if (!interleaved.has(key)) {
        interleaved.add(key)
        bytes += accessor.count * stride
      }
    } else {
      bytes += accessor.count * element
    }
  }
  for (const mesh of json.meshes)
    for (const primitive of mesh.primitives) {
      const compressed = draco && primitive.extensions?.KHR_draco_mesh_compression
      if (compressed) {
        for (const semantic of Object.keys(compressed.attributes ?? {})) {
          const accessor = json.accessors[primitive.attributes[semantic]]
          if (!accessor) fail('Draco attribute accessor 无效', 'KHR_draco_mesh_compression')
          const component = COMPONENT_BYTE_SIZES[accessor.componentType]
          const count = ACCESSOR_COMPONENT_COUNTS[accessor.type]
          const tight = component * count
          bytes += accessor.count * (Math.ceil(tight / 4) * 4 - tight)
        }
        const index = json.accessors[primitive.indices]
        if (index) bytes += index.count * (4 - COMPONENT_BYTE_SIZES[index.componentType])
      }
      const count = json.accessors[primitive.indices ?? primitive.attributes.POSITION].count
      if (!compressed && (primitive.mode === 5 || primitive.mode === 6))
        bytes += Math.max(0, count - 2) * 3 * 4
      if (primitive.indices === undefined) bytes += count * 4
      if (primitive.attributes.NORMAL === undefined)
        bytes += json.accessors[primitive.attributes.POSITION].count * 12
    }
  if (!Number.isSafeInteger(bytes)) fail('glTF 几何成本超出可表示范围')
  return bytes
}

export function estimateManifestCosts(
  resource: TileResourceData,
  manifest: GltfManifest,
  textureSizing: TextureSizing = resolveTextureSizing()
): TileResourceCosts {
  const { json, binary } = manifest
  const geometryBytes = estimateGltfGeometryBytes(
    json,
    manifest.dracoBytes !== undefined && manifest.dracoBytes > 0
  )
  let inputBytes = resource.data.byteLength
  const buffers: Array<ArrayBuffer | Uint8Array<ArrayBuffer>> = []
  const inputUris = new Set<string>()
  for (const [index, buffer] of (json.buffers ?? []).entries()) {
    if (buffer.uri?.startsWith('data:')) buffers[index] = decodeDataUri(buffer.uri)
    else if (buffer.uri === undefined && index === 0 && binary) buffers[index] = binary
    if (buffer.uri !== undefined) {
      const uri = buffer.uri.startsWith('data:')
        ? buffer.uri
        : new URL(buffer.uri, resource.url).href
      if (!inputUris.has(uri)) {
        inputUris.add(uri)
        inputBytes += buffer.byteLength
      }
    }
  }
  let textureBytes = 0
  let cpuTextureBytes = 0
  for (const image of json.images ?? []) {
    let bytes: ArrayBuffer | Uint8Array<ArrayBuffer> | undefined
    if (image.uri?.startsWith('data:')) bytes = decodeDataUri(image.uri)
    else if (
      image.bufferView !== undefined &&
      !json.bufferViews?.[image.bufferView]?.extensions?.[MESHOPT_EXTENSION] &&
      buffers[json.bufferViews?.[image.bufferView]?.buffer]
    )
      bytes = getBufferViewBytes(json, buffers, image.bufferView)
    if (bytes) {
      const info = getTextureDimensions(readImageInfo(bytes), textureSizing)
      cpuTextureBytes += info.width * info.height * 4
      textureBytes += Math.ceil((info.width * info.height * 4 * 4) / 3)
    }
  }
  // 输出、Three 视图/属性临时拷贝及借用 decoder 的保守工作空间；不声称精确 WASM 用量。
  return {
    inputBytes,
    geometryBytes,
    textureBytes,
    cpuTextureBytes,
    gpuTextureBytes: textureBytes,
    transientBytes:
      inputBytes + geometryBytes + manifest.meshoptBytes * 3 + (manifest.dracoBytes ?? 0),
  }
}
